import type { PropsWithChildren } from "react";
import { useCallback, createContext, useContext, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import noop from "lodash/noop";

import { getAllDomains } from "app/pages/activation/helpers/getAllDomains";
import { getExistingNames } from "app/pages/activation/helpers/getExistingNames";
import { getSortedProjects } from "app/pages/activation/helpers/home/getSortedProjects";
import type { IWebsitesFavicons } from "app/services/sitesService/types";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";

import { ONE } from "../../components/Header/constants";
import { ActivationComponentAssets } from "../../helpers/ActivationAssets";
import getIsActivationLimitReached from "../../helpers/getIsActivationLimitReached";
import useActivationSetListQuery from "../../queries/data/useActivationSetListQuery";
import { useIconsQuery } from "../../queries/useIconsQuery";
import type { ActivationMetadataItem, ActivationSetSchema } from "../../types/api";

export type HomeContextSchema = {
    images?: IWebsitesFavicons;
    activationId?: string;
    existingNames: (string | undefined)[];
    isImagesLoading: boolean;
    isImagesFetching: boolean;
    isActivationSetsExists: boolean;
    isActivationLimitReached: boolean;
    isBannerShowed: boolean;
    isActivationLoading: boolean;
    totalNumber?: number;
    projects: ActivationSetSchema[];
    isError: boolean;
    isAllAvailableReturned: boolean;
    activeActivationId?: string;
    activeActivation?: ActivationMetadataItem;
    onSelectActivation: (activation: ActivationMetadataItem) => void;
    refetchActivations: () => void;
    getAllData: () => void;
};

const initialState: HomeContextSchema = {
    images: undefined,
    activationId: undefined,
    existingNames: [],
    isImagesLoading: false,
    isImagesFetching: false,
    isActivationSetsExists: false,
    isActivationLimitReached: false,
    isBannerShowed: false,
    isActivationLoading: false,
    totalNumber: undefined,
    projects: [],
    isError: false,
    isAllAvailableReturned: false,
    activeActivationId: undefined,
    activeActivation: undefined,
    onSelectActivation: noop,
    refetchActivations: noop,
    getAllData: noop,
};

const HomeContext = createContext(initialState);

export const HomeContextProvider = ({ children }: PropsWithChildren<{}>) => {
    const {
        params: { key: activationId },
    } = useCurrentStateAndParams();
    const [isFetchEnabled, setIsFetchEnabled] = useState(false);
    const [isGetAllData, setIsGetAllData] = useState(false);

    const [activeActivation, setActiveActivation] = useState<ActivationMetadataItem | undefined>(undefined);

    const activeActivationId = useMemo(() => (activeActivation ? activeActivation?.id : undefined), [activeActivation]);

    const { data, isLoading, isFetching, isError, refetch } = useActivationSetListQuery(
        getSortedProjects,
        isFetchEnabled,
        isGetAllData,
    );

    const isActivationLoading = isLoading || isFetching;

    useEffect(() => {
        setIsFetchEnabled(true);
        setIsGetAllData((prev) => prev || !!data?.isAllAvailableReturned);
    }, [data]);

    const domains = useMemo(() => data && getAllDomains(data?.activationSets), [data]);
    const existingNames = useMemo(() => data && getExistingNames(data?.activationSets), [data]);

    const isTrialOrAfter = isTrialOrAfterTrial();

    const {
        data: images,
        isLoading: isImagesLoading,
        isFetching: isImagesFetching,
    } = useIconsQuery(domains, !isActivationLoading);

    const activationLimit = ActivationComponentAssets.activationSetsLimitation;

    const isActivationSetsExists = data && !!data.activationSets.length;

    const isActivationLimitReached = getIsActivationLimitReached(data?.totalNumber);
    const isBannerShowed = activationLimit === ONE && !isTrialOrAfter;

    const onSelectActivation = useCallback((activation: ActivationMetadataItem) => {
        setActiveActivation(activation);
    }, []);

    const getAllData = () => {
        if (isGetAllData || isActivationLoading) {
            return;
        }

        setIsFetchEnabled(true);
        setIsGetAllData(true);

        setTimeout(refetch, 100);
    };

    return (
        <HomeContext.Provider
            value={{
                images,
                activationId,
                existingNames,
                isImagesLoading,
                isImagesFetching,
                isActivationSetsExists,
                isActivationLoading,
                isActivationLimitReached,
                isBannerShowed,
                projects: data?.activationSets,
                totalNumber: data?.totalNumber,
                isAllAvailableReturned: data?.isAllAvailableReturned,
                isError,
                activeActivationId,
                activeActivation,
                onSelectActivation,
                refetchActivations: refetch,
                getAllData,
            }}
        >
            {children}
        </HomeContext.Provider>
    );
};

export const useHomeContext = () => {
    const context = useContext(HomeContext);

    if (context === undefined) {
        throw new Error("useHomeContext must be used within a HomeContext");
    }

    return context;
};
